import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Crown, Calendar, CreditCard, AlertCircle, TrendingUp, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionDashboard() {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSubscription(response.data.subscription);
      }
    } catch (error) {
      console.error('Failed to load subscription:', error);
      toast.error('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
      return;
    }

    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Subscription cancelled successfully');
      loadSubscription();
    } catch (error) {
      console.error('Failed to cancel:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const tierNames = {
    free: 'Free',
    pro_lite: 'Pro Lite',
    pro_max: 'Pro Max'
  };

  const tierColors = {
    free: 'bg-gray-100 text-gray-800',
    pro_lite: 'bg-blue-100 text-blue-800',
    pro_max: 'bg-purple-100 text-purple-800'
  };

  const features = subscription?.features || {};

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription & Usage</h1>
          <p className="text-gray-600">Manage your subscription and track your usage</p>
        </div>

        {/* Current Plan Card */}
        <Card className="mb-8 border-2 border-blue-500">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Crown className="w-8 h-8 text-yellow-500" />
                <div>
                  <CardTitle className="text-2xl">Current Plan</CardTitle>
                  <span className={`inline-block mt-2 px-4 py-1 rounded-full text-sm font-semibold ${tierColors[subscription?.tier] || tierColors.free}`}>
                    {tierNames[subscription?.tier] || 'Free'}
                  </span>
                </div>
              </div>
              {subscription?.tier !== 'free' && subscription?.tier !== 'pro_max' && (
                <Button onClick={() => navigate('/pricing')} className="bg-gradient-to-r from-blue-600 to-purple-600">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Upgrade
                </Button>
              )}
              {subscription?.tier === 'free' && (
                <Button onClick={() => navigate('/pricing')} className="bg-gradient-to-r from-blue-600 to-purple-600">
                  Upgrade to Pro
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm font-semibold">Status</span>
                </div>
                <p className="text-lg font-bold capitalize">
                  {subscription?.status || 'Active'}
                </p>
              </div>
              {subscription?.startDate && (
                <div>
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <CreditCard className="w-4 h-4" />
                    <span className="text-sm font-semibold">Start Date</span>
                  </div>
                  <p className="text-lg font-bold">
                    {new Date(subscription.startDate).toLocaleDateString()}
                  </p>
                </div>
              )}
              {subscription?.endDate && (
                <div>
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-semibold">Renews On</span>
                  </div>
                  <p className="text-lg font-bold">
                    {new Date(subscription.endDate).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            {subscription?.tier !== 'free' && (
              <div className="mt-6 pt-6 border-t">
                <Button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feature Limits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Events */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">📅 Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {features.events?.unlimited ? 'Unlimited' : 'Limited'}
              </div>
              <p className="text-sm text-gray-600 mt-2">Create and manage events</p>
            </CardContent>
          </Card>

          {/* CRM */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">👥 CRM Accounts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {features.crm_accounts?.unlimited ? 'Unlimited' : `${features.crm_accounts?.max || 0} max`}
              </div>
              <p className="text-sm text-gray-600 mt-2">Manage customer relationships</p>
            </CardContent>
          </Card>

          {/* Websites */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">🌐 Websites</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {features.websites?.unlimited ? 'Unlimited' : `${features.websites?.max || 0} max`}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Custom Domain: {features.websites?.customDomain ? '✓ Enabled' : '✗ Disabled'}
              </p>
            </CardContent>
          </Card>

          {/* Digi Strategy */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">📊 Digi Strategy</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">
                {features.digiStrategy?.unlimited ? 'Unlimited' : `${features.digiStrategy?.max || 0}/month`}
              </div>
              <p className="text-sm text-gray-600 mt-2">Generate business strategies</p>
            </CardContent>
          </Card>

          {/* Digi Presentor */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">🎤 Digi Presentor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-pink-600">
                {features.digiPresentor?.unlimited ? 'Unlimited' : `${features.digiPresentor?.max || 0}/month`}
              </div>
              <p className="text-sm text-gray-600 mt-2">Create presentations</p>
            </CardContent>
          </Card>

          {/* Digi Editor */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">✨ Digi Editor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-indigo-600">
                {features.digiEditor?.unlimited ? 'Unlimited' : `${features.digiEditor?.max || 0}/day`}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Watermark: {features.digiEditor?.watermark ? '✓ Added' : '✗ None'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Upgrade CTA */}
        {subscription?.tier === 'free' && (
          <Card className="mt-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
            <CardContent className="py-12 text-center">
              <Crown className="w-16 h-16 mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-4">Unlock Full Potential</h2>
              <p className="text-xl mb-6 opacity-90">
                Upgrade to Pro and remove all limitations
              </p>
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg"
              >
                View Pricing Plans
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <div className="text-center mt-8">
          <Button onClick={() => navigate('/')} variant="outline">
            ← Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
